import React from "react";
import { ElevacionTecnica, MODEL_ACCENT, exportExpedientePDF } from "./VmPlanoTecnico";
import { VM_FACADES } from "./vmExports";
import PlantaModeloAFuncional from "./VmPlantaModeloA";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import {
  MousePointer2, Hand, RotateCw, Ruler, ZoomIn, ZoomOut, Maximize2, Minimize2,
  Crosshair, FileDown, Layers as LayersIcon, Eye, EyeOff,
  Sparkles, Terminal, X, CornerUpRight, Type as TypeIcon,
} from "lucide-react";

// ── COREX · Lienzo Maestro "Blueprint Dark" (grado industrial) ────────────────
// Zoom (rueda), Pan (arrastre), Maximizar (pantalla completa), rejilla técnica,
// estructura en cian con glow y panel de Capas (EST/ARQ/ACOT/MOB) funcional.

const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CYAN = "#22d3ee", CYAN_HOT = "#00ffff", WALL = "#7dd3fc", INKL = "#cbd5e1", DIMC = "#38bdf8", BG = "#0e1621";

// Treemap squarificado: reparte ambientes proporcional a su área dentro del rectángulo.
function squarify(items, x, y, w, h) {
  const total = items.reduce((a, b) => a + Math.max(0.001, b.value), 0) || 1;
  const scale = (w * h) / total;
  const rem = items.map((i) => ({ ...i, area: Math.max(0.001, i.value) * scale }));
  const rect = { x, y, w, h };
  const out = [];
  const worst = (row, len) => { const s = row.reduce((a, b) => a + b.area, 0); const mx = Math.max(...row.map((r) => r.area)); const mn = Math.min(...row.map((r) => r.area)); return Math.max((len * len * mx) / (s * s), (s * s) / (len * len * mn)); };
  const layout = (row, horizontal) => {
    const s = row.reduce((a, b) => a + b.area, 0);
    if (horizontal) { const rh = s / rect.w; let cx = rect.x; row.forEach((r) => { const rw = r.area / rh; out.push({ ...r, x: cx, y: rect.y, w: rw, h: rh }); cx += rw; }); rect.y += rh; rect.h -= rh; }
    else { const rw = s / rect.h; let cy = rect.y; row.forEach((r) => { const rh2 = r.area / rw; out.push({ ...r, x: rect.x, y: cy, w: rw, h: rh2 }); cy += rh2; }); rect.x += rw; rect.w -= rw; }
  };
  let row = [];
  while (rem.length) {
    const horizontal = rect.w >= rect.h, len = horizontal ? rect.w : rect.h;
    if (!row.length) { row.push(rem.shift()); continue; }
    if (worst(row, len) >= worst([...row, rem[0]], len)) row.push(rem.shift());
    else { layout(row, horizontal); row = []; }
  }
  if (row.length) layout(row, rect.w >= rect.h);
  return out;
}

/* Planta técnica en estética blueprint, organizada por CAPAS. */
function PlantaBlueprint({ ancho, largo, uso = "COCHERA", modeloId = "A", layers, ambientes = [], sel = null, onSelectRoom, onHandleDown, editable = false }) {
  const A = Math.max(3, Number(ancho) || 7.5), L = Math.max(4, Number(largo) || 20);
  const VW = 460, VH = 340, mL = 50, mT = 40, mR = 34, mB = 34;
  const s = Math.min((VW - mL - mR) / A, (VH - mT - mB) / L);
  const w = A * s, h = L * s, x0 = (VW - w) / 2, y0 = (VH - h) / 2 + 4;
  const W25 = 0.25 * s, W15 = 0.15 * s;
  const accent = MODEL_ACCENT[modeloId] || MODEL_ACCENT.A;
  const ix = x0 + W25, iy = y0 + W25, iw = w - 2 * W25, ih = h - 2 * W25;
  const stairW = Math.min(iw * 0.34, 3.0 * s);
  const rF = ih * 0.24, rS = ih * 0.34, rR = ih - rF - rS;
  const yF = iy, yS = iy + rF, yR = iy + rF + rS;
  const kW = iw * 0.30, bW = iw * 0.28;
  // Mapa de ambientes de la tabla (Bloque 4) → bloques proporcionales (treemap).
  const ambList = (ambientes || []).filter((a) => (Number(a.m2) || 0) > 0);
  const rooms = ambList.length ? squarify(ambList.map((a) => ({ nombre: a.nombre, m2: Number(a.m2) || 0, libre: a.libre, value: Number(a.m2) || 0 })), ix, iy, iw, ih) : [];
  const arc = (cx, cy, r, dir = 1, rot = 0) => {
    const rad = (d) => (d * Math.PI) / 180, a1 = rot + 90 * dir;
    const p1 = [cx + r * Math.cos(rad(rot)), cy + r * Math.sin(rad(rot))], p2 = [cx + r * Math.cos(rad(a1)), cy + r * Math.sin(rad(a1))];
    return <g stroke={WALL} strokeWidth="0.7" fill="none"><line x1={cx} y1={cy} x2={p1[0]} y2={p1[1]} strokeWidth="1.2" /><path d={`M ${p1[0]} ${p1[1]} A ${r} ${r} 0 0 ${dir > 0 ? 1 : 0} ${p2[0]} ${p2[1]}`} strokeDasharray="2 2" /></g>;
  };
  // Malla de columnas (grilla estructural): esquinas + intermedias
  const colXs = [x0 + W25 / 2, x0 + w / 2, x0 + w - W25 / 2];
  const colYs = [y0 + W25 / 2, y0 + h * 0.33, y0 + h * 0.66, y0 + h - W25 / 2];
  const colSz = Math.max(4, 0.30 * s);
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full block select-none" data-testid="vm-planta-tecnica" role="img" aria-label="Planta blueprint">
      <defs>
        <pattern id="bpFine" width="12" height="12" patternUnits="userSpaceOnUse"><path d="M12 0H0V12" fill="none" stroke="#ffffff" strokeOpacity="0.05" strokeWidth="0.5" /></pattern>
        <pattern id="bpCoarse" width="60" height="60" patternUnits="userSpaceOnUse"><path d="M60 0H0V60" fill="none" stroke="#38bdf8" strokeOpacity="0.10" strokeWidth="0.7" /></pattern>
        <filter id="bpGlow" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="2.4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <rect x="0" y="0" width={VW} height={VH} fill={BG} />
      <rect x="0" y="0" width={VW} height={VH} fill="url(#bpFine)" />
      <rect x="0" y="0" width={VW} height={VH} fill="url(#bpCoarse)" />

      {/* ── CAPA ARQ: muros + vanos + zonas ── */}
      {layers.ARQ && (
        <g>
          {/* Muro perimetral 0.25 (carga) */}
          <rect x={x0} y={y0} width={w} height={h} fill="rgba(125,211,252,0.05)" stroke={WALL} strokeWidth="1.6" />
          <rect x={ix} y={iy} width={iw} height={ih} fill="none" stroke={WALL} strokeWidth="0.8" />
          {rooms.length ? (
            // ── Habitaciones mapeadas desde la tabla del Bloque 4 (muros, puertas, ventanas) ──
            <g>
              {rooms.map((r, i) => {
                const label = (r.nombre || "").toUpperCase();
                const tL = r.x <= ix + 1, tR = r.x + r.w >= ix + iw - 1, tT = r.y <= iy + 1, tB = r.y + r.h >= iy + ih - 1;
                const isFrontRoom = /cochera|tienda|ingreso|local|comerc/i.test(r.nombre || "");
                const fill = r.libre ? "rgba(56,189,248,0.05)" : (isFrontRoom && uso === "TIENDA") ? "rgba(34,211,238,0.12)" : "rgba(125,211,252,0.04)";
                const rad = Math.min(12, Math.min(r.w, r.h) * 0.32);
                let door = null;
                if (!isFrontRoom) {
                  if (!tB) door = arc(r.x + r.w * 0.5, r.y + r.h, rad, -1, 180);
                  else if (!tT) door = arc(r.x + r.w * 0.5, r.y, rad, 1, 0);
                  else if (!tL) door = arc(r.x, r.y + r.h * 0.5, rad, 1, -90);
                  else if (!tR) door = arc(r.x + r.w, r.y + r.h * 0.5, rad, -1, 90);
                }
                let win = null;
                if (tT && r.w > 18) win = <g stroke={accent} strokeWidth="1.2"><line x1={r.x + r.w * 0.3} y1={iy} x2={r.x + r.w * 0.7} y2={iy} /><line x1={r.x + r.w * 0.3} y1={iy + 1.7} x2={r.x + r.w * 0.7} y2={iy + 1.7} /></g>;
                else if (tL && r.h > 18) win = <g stroke={accent} strokeWidth="1.2"><line x1={ix} y1={r.y + r.h * 0.3} x2={ix} y2={r.y + r.h * 0.7} /><line x1={ix + 1.7} y1={r.y + r.h * 0.3} x2={ix + 1.7} y2={r.y + r.h * 0.7} /></g>;
                else if (tR && r.h > 18) win = <g stroke={accent} strokeWidth="1.2"><line x1={ix + iw} y1={r.y + r.h * 0.3} x2={ix + iw} y2={r.y + r.h * 0.7} /><line x1={ix + iw - 1.7} y1={r.y + r.h * 0.3} x2={ix + iw - 1.7} y2={r.y + r.h * 0.7} /></g>;
                return (
                  <g key={i}>
                    <rect x={r.x + 0.4} y={r.y + 0.4} width={Math.max(0, r.w - 0.8)} height={Math.max(0, r.h - 0.8)} fill={fill} stroke={sel === i ? "#22d3ee" : WALL} strokeWidth={sel === i ? 2.2 : Math.max(1.3, W15)} strokeOpacity="0.95" />
                    {door}{win}
                    {/* Anti-solape: nombre solo si hay altura; m² siempre que quepa; cota interna solo en grandes */}
                    {r.w > 20 && r.h > 12 && (
                      <g textAnchor="middle">
                        {r.h > 24 && r.w > 30 && <text x={r.x + r.w / 2} y={r.y + r.h / 2 - 2.5} fill={r.libre ? "#7dd3fc" : INKL} fontSize={Math.max(5.5, Math.min(7.5, r.w / 8))} fontWeight="700">{label.length > 13 ? label.slice(0, 12) + "…" : label}</text>}
                        <text x={r.x + r.w / 2} y={r.y + r.h / 2 + (r.h > 24 && r.w > 30 ? 6 : 2.5)} fill={CYAN} fontSize={Math.max(5, Math.min(6.5, r.w / 9))} fontWeight="800">{r.m2.toFixed(2)} m²</text>
                      </g>
                    )}
                    {/* Editor bidireccional: hit-rect (selección) + manejadores de muro */}
                    {editable && <rect x={r.x} y={r.y} width={r.w} height={r.h} fill="transparent" style={{ pointerEvents: "auto", cursor: "pointer" }} onPointerDown={(e) => { e.stopPropagation(); onSelectRoom && onSelectRoom(i); }} data-testid={`vm-room-${i}`} />}
                    {editable && sel === i && !r.libre && [["r", "x", r.x + r.w, r.y + r.h / 2], ["l", "x", r.x, r.y + r.h / 2], ["b", "y", r.x + r.w / 2, r.y + r.h], ["t", "y", r.x + r.w / 2, r.y]].map(([edge, axis, hx, hy]) => (
                      <rect key={edge} x={hx - 3.2} y={hy - 3.2} width="6.4" height="6.4" fill="#22d3ee" stroke="#fff" strokeWidth="0.9" data-testid={`vm-handle-${i}-${edge}`}
                        style={{ pointerEvents: "auto", cursor: axis === "x" ? "ew-resize" : "ns-resize" }}
                        onPointerDown={(e) => { e.stopPropagation(); onHandleDown && onHandleDown(i, axis, edge, r, e); }} />
                    ))}
                  </g>
                );
              })}
            </g>
          ) : (
            // ── Esquema paramétrico por defecto (sin ambientes en tabla) ──
            <g>
              {uso === "TIENDA" && <rect x={ix} y={yF} width={iw} height={rF} fill="rgba(34,211,238,0.10)" />}
              <g fill={WALL} opacity="0.7">
                <rect x={ix} y={yS - W15 / 2} width={iw} height={W15} />
                <rect x={ix} y={yR - W15 / 2} width={iw} height={W15} />
                <rect x={ix + iw - stairW - W15 / 2} y={yS} width={W15} height={ih - rF} />
                <rect x={ix + kW - W15 / 2} y={yS} width={W15} height={rS} />
                <rect x={ix + iw - stairW - bW - W15 / 2} y={yR} width={W15} height={rR} />
              </g>
              {arc(ix + W15 + 4, yS - W15 / 2, 12, 1, -90)}
              {arc(ix + kW + 6, yS + rS * 0.5, 11, 1, 90)}
              {arc(ix + iw - stairW - bW + 5, yR - W15 / 2, 10, 1, -90)}
              <g fill={INKL} fontSize="7.5" fontWeight="700" textAnchor="middle">
                <text x={ix + kW * 0.5} y={yS + rS * 0.5} fill={CYAN}>COCINA</text>
                <text x={ix + kW + (iw - kW - stairW) * 0.5} y={yS + rS * 0.5}>SALA · COMEDOR</text>
                <text x={ix + (iw - stairW - bW) * 0.5} y={yR + rR * 0.55}>DORMITORIO</text>
              </g>
            </g>
          )}
          {/* frente según uso (portón / mampara) */}
          {uso === "COCHERA" ? (
            <g>{arc(ix + iw - stairW - 4, y0 + h - 2, 13, -1, 180)}<rect x={ix + 4} y={y0 + h - W25 - 1.5} width={iw * 0.52} height={2.4} fill={accent} /></g>
          ) : (
            <g><rect x={ix + 4} y={y0 + h - W25 - 1} width={iw - 8} height={2.2} fill={CYAN} />{arc(ix + iw * 0.5, y0 + h - 2, 12, -1, 180)}</g>
          )}
        </g>
      )}

      {/* ── CAPA MOB: mobiliario / zonas húmedas ── */}
      {layers.MOB && (
        rooms.length ? (
          <g stroke={CYAN} strokeWidth="0.7" fill="rgba(34,211,238,0.14)">
            {rooms.map((r, i) => {
              const nm = (r.nombre || "").toLowerCase();
              if (r.w < 20 || r.h < 14) return null;
              const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
              if (/cochera|ingreso/.test(nm) && uso === "COCHERA") return (
                <g key={i}>
                  <rect x={cx - r.w * 0.28} y={cy - r.h * 0.17} width={r.w * 0.56} height={r.h * 0.34} rx="3" fill="rgba(34,211,238,0.18)" />
                  <rect x={cx - r.w * 0.15} y={cy - r.h * 0.13} width={r.w * 0.3} height={r.h * 0.15} rx="2" />
                  <circle cx={cx - r.w * 0.16} cy={cy + r.h * 0.17} r="2.2" fill={CYAN} />
                  <circle cx={cx + r.w * 0.16} cy={cy + r.h * 0.17} r="2.2" fill={CYAN} />
                </g>
              );
              if (/tienda|local|comerc/.test(nm) && uso === "TIENDA") return <text key={i} x={cx} y={cy + 9} textAnchor="middle" fill={CYAN} fontSize="6" fontWeight="800" stroke="none">LOCAL COMERCIAL</text>;
              if (/cocina/.test(nm)) return <g key={i}><rect x={r.x + 3} y={r.y + 3} width={Math.max(6, r.w - 6)} height={5} /><circle cx={r.x + 8} cy={r.y + 5.5} r="1.5" fill="none" stroke={INKL} /></g>;
              if (/ba[ñn]o|ss\.?hh|servicio/.test(nm)) return <g key={i}><rect x={r.x + 3} y={r.y + 3} width={6} height={8} rx="2.5" /><rect x={r.x + 3} y={r.y + r.h - 7} width={7} height={4} rx="1" /></g>;
              if (/dormitorio|habitaci/.test(nm)) { const bw = Math.min(20, r.w * 0.55), bh = Math.min(16, r.h * 0.55); return <g key={i}><rect x={r.x + 3} y={r.y + r.h - 3 - bh} width={bw} height={bh} rx="1" /><rect x={r.x + 3} y={r.y + r.h - 3 - bh} width={bw} height={bh * 0.28} fill="rgba(34,211,238,0.32)" /></g>; }
              if (/escalera|pasa/.test(nm)) return <g key={i} stroke={INKL} strokeWidth="0.5" fill="none">{Array.from({ length: 6 }, (_, k) => <line key={k} x1={r.x + 2} y1={r.y + 2 + (r.h / 6) * (k + 1)} x2={r.x + r.w - 2} y2={r.y + 2 + (r.h / 6) * (k + 1)} />)}</g>;
              return null;
            })}
          </g>
        ) : (
          <g stroke={CYAN} strokeWidth="0.8" fill="rgba(34,211,238,0.10)">
            <rect x={ix + 3} y={yS + 3} width={kW - 8} height={7} />
            <rect x={ix + 3} y={yS + 3} width={7} height={rS - 8} />
            <rect x={ix + iw - stairW - bW + 3} y={yR + 4} width={7} height={9} rx="2" />
            <g stroke={INKL} strokeWidth="0.5" fill="none">
              <rect x={ix + iw - stairW} y={yS} width={stairW} height={ih - rF} />
              {Array.from({ length: 9 }, (_, i) => <line key={i} x1={ix + iw - stairW} y1={yS + ((ih - rF) / 9) * (i + 1)} x2={ix + iw} y2={yS + ((ih - rF) / 9) * (i + 1)} />)}
            </g>
          </g>
        )
      )}

      {/* ── CAPA EST: columnas + vigas con GLOW cian ── */}
      {layers.EST && (
        <g filter="url(#bpGlow)">
          {/* vigas perimetrales (ejes) */}
          <rect x={x0} y={y0} width={w} height={h} fill="none" stroke={CYAN_HOT} strokeWidth="1.1" opacity="0.9" />
          {colYs.map((cy, i) => <line key={"vy" + i} x1={x0} y1={cy} x2={x0 + w} y2={cy} stroke={CYAN} strokeWidth="0.8" opacity="0.55" />)}
          <line x1={x0 + w / 2} y1={y0} x2={x0 + w / 2} y2={y0 + h} stroke={CYAN} strokeWidth="0.8" opacity="0.55" />
          {/* columnas */}
          {colXs.map((cx) => colYs.map((cy, j) => (
            <rect key={cx + "-" + j} x={cx - colSz / 2} y={cy - colSz / 2} width={colSz} height={colSz} fill={CYAN_HOT} stroke="#a5f3fc" strokeWidth="0.6" />
          )))}
        </g>
      )}

      {/* ── CAPA ACOT: cotas ── */}
      {layers.ACOT && (
        <g stroke={DIMC} strokeWidth="0.8" fill={DIMC} fontSize="7.5" fontWeight="800">
          <line x1={x0} y1={y0 - 16} x2={x0 + w} y2={y0 - 16} /><line x1={x0} y1={y0 - 19} x2={x0} y2={y0 - 13} /><line x1={x0 + w} y1={y0 - 19} x2={x0 + w} y2={y0 - 13} />
          <text x={x0 + w / 2} y={y0 - 19} textAnchor="middle" stroke="none">{n2(A)} m</text>
          <line x1={x0 - 18} y1={y0} x2={x0 - 18} y2={y0 + h} /><line x1={x0 - 21} y1={y0} x2={x0 - 15} y2={y0} /><line x1={x0 - 21} y1={y0 + h} x2={x0 - 15} y2={y0 + h} />
          <text x={x0 - 22} y={y0 + h / 2} textAnchor="middle" stroke="none" transform={`rotate(-90 ${x0 - 22} ${y0 + h / 2})`}>{n2(L)} m</text>
          {rooms.length ? (
            // Cotas internas automáticas por ambiente (ancho × largo interno en m)
            rooms.map((r, i) => (r.w > 34 && r.h > 24) ? (
              <g key={"di" + i} strokeOpacity="0.45" fillOpacity="0.85" fontWeight="700">
                <text x={r.x + r.w / 2} y={r.y + 8} textAnchor="middle" stroke="none" fontSize="5.6">{n2(r.w / s)}</text>
                <text x={r.x + 7} y={r.y + r.h / 2} textAnchor="middle" stroke="none" fontSize="5.6" transform={`rotate(-90 ${r.x + 7} ${r.y + r.h / 2})`}>{n2(r.h / s)}</text>
              </g>
            ) : null)
          ) : (
            [["", yF, yS, rF], ["", yS, yR, rS], ["", yR, iy + ih, rR]].map(([_, a, b], i) => (
              <g key={i}><line x1={x0 + w + 12} y1={a} x2={x0 + w + 12} y2={b} /><line x1={x0 + w + 9} y1={a} x2={x0 + w + 15} y2={a} /><line x1={x0 + w + 9} y1={b} x2={x0 + w + 15} y2={b} /><text x={x0 + w + 13} y={(a + b) / 2} textAnchor="middle" stroke="none" fontSize="6.5" transform={`rotate(-90 ${x0 + w + 13} ${(a + b) / 2})`}>{n2((b - a) / s)}</text></g>
            ))
          )}
        </g>
      )}
      <text x={x0} y={VH - 8} fill="#64748b" fontSize="7" fontWeight="700">MODELO {modeloId} · {uso} · MUROS 0.25/0.15 m · EST cian · cotas en m</text>
    </svg>
  );
}

/* ── CORTE A-A multinivel (sección: vigas, losa aligerada 20cm, N.P.T.) ── */
function CorteAA({ largo, pisos = 1, altura1 = 3.0, alturaTipica = 2.7, incluirParapeto = true, parapetoH = 0.9 }) {
  const N = Math.max(1, Number(pisos) || 1), Lp = Math.max(4, Number(largo) || 20);
  const h1 = Number(altura1) || 3.0, ht = Number(alturaTipica) || 2.7, hp = Number(parapetoH) || 0.9;
  const heights = [h1, ...Array.from({ length: N - 1 }, () => ht)];
  const npt = [0]; heights.forEach((h) => npt.push(npt[npt.length - 1] + h));
  const totalH = npt[npt.length - 1] + (incluirParapeto ? hp * 0.5 : 0);
  const VW = 460, VH = 340, mL = 58, mT = 24, mB = 30, mR = 24;
  const s = Math.min((VW - mL - mR) / Lp, (VH - mT - mB) / totalH);
  const w = Lp * s, baseY = VH - mB, x0 = (VW - w) / 2 + 12;
  const slab = Math.max(2.5, 0.20 * s), beam = Math.max(4, 0.40 * s), wallT = Math.max(2.5, 0.25 * s);
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full block select-none" data-testid="vm-corte-aa" role="img" aria-label="Corte A-A">
      <defs>
        <pattern id="ctFine" width="12" height="12" patternUnits="userSpaceOnUse"><path d="M12 0H0V12" fill="none" stroke="#ffffff" strokeOpacity="0.05" strokeWidth="0.5" /></pattern>
        <filter id="ctGlow" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>
      <rect x="0" y="0" width={VW} height={VH} fill={BG} /><rect x="0" y="0" width={VW} height={VH} fill="url(#ctFine)" />
      <line x1={x0 - 24} y1={baseY} x2={x0 + w + 14} y2={baseY} stroke={INKL} strokeWidth="1.4" />
      {/* Muros perimetrales cortados (izq/der) */}
      <rect x={x0} y={baseY - npt[N] * s} width={wallT} height={npt[N] * s} fill="rgba(125,211,252,0.10)" stroke={WALL} strokeWidth="0.8" />
      <rect x={x0 + w - wallT} y={baseY - npt[N] * s} width={wallT} height={npt[N] * s} fill="rgba(125,211,252,0.10)" stroke={WALL} strokeWidth="0.8" />
      {heights.map((hh, i) => {
        const yLevel = baseY - npt[i + 1] * s;
        return (
          <g key={i}>
            {/* Losa aligerada 20 cm (con viguetas) */}
            <g filter="url(#ctGlow)">
              <rect x={x0} y={yLevel} width={w} height={slab} fill={CYAN_HOT} opacity="0.85" />
              {/* Peralte de viga en apoyos (extremos) */}
              <rect x={x0} y={yLevel} width={beam * 0.5} height={beam} fill={CYAN_HOT} opacity="0.95" />
              <rect x={x0 + w - beam * 0.5} y={yLevel} width={beam * 0.5} height={beam} fill={CYAN_HOT} opacity="0.95" />
            </g>
            {/* Viguetas (ladrillos de techo) */}
            {Array.from({ length: Math.max(4, Math.floor(w / 14)) }, (_, k) => <line key={k} x1={x0 + 4 + k * 14} y1={yLevel + slab} x2={x0 + 4 + k * 14} y2={yLevel + slab + 2.4} stroke={CYAN} strokeWidth="0.7" opacity="0.6" />)}
            {/* N.P.T. del nivel */}
            <line x1={x0 - 40} y1={yLevel + slab} x2={x0} y2={yLevel + slab} stroke={DIMC} strokeWidth="0.7" />
            <text x={x0 - 40} y={yLevel + slab - 2} fill={DIMC} fontSize="7" fontWeight="800">N.P.T. +{npt[i + 1].toFixed(2)}</text>
            {/* rótulo de nivel */}
            <text x={x0 + w / 2} y={(baseY - npt[i] * s + yLevel) / 2} fill={INKL} fontSize="7" fontWeight="700" textAnchor="middle">NIVEL {i + 1}</text>
            {/* peralte etiqueta */}
            {i === 0 && <text x={x0 + beam + 3} y={yLevel + beam - 1} fill={CYAN} fontSize="5.5" fontWeight="700">VIGA h=0.40</text>}
          </g>
        );
      })}
      {/* N.P.T. terreno */}
      <line x1={x0 - 40} y1={baseY} x2={x0} y2={baseY} stroke={DIMC} strokeWidth="0.7" /><text x={x0 - 40} y={baseY - 2} fill={DIMC} fontSize="7" fontWeight="800">N.P.T. ±0.00</text>
      {incluirParapeto && <rect x={x0} y={baseY - totalH * s} width={w} height={hp * 0.5 * s} fill="none" stroke="#94a3b8" strokeDasharray="3 2" strokeWidth="0.8" />}
      {/* Cota total */}
      <g stroke={DIMC} strokeWidth="0.8"><line x1={x0 + w + 12} y1={baseY - npt[N] * s} x2={x0 + w + 12} y2={baseY} /><text x={x0 + w + 13} y={baseY - (npt[N] * s) / 2} fill={DIMC} fontSize="7" fontWeight="800" textAnchor="middle" stroke="none" transform={`rotate(-90 ${x0 + w + 13} ${baseY - (npt[N] * s) / 2})`}>{npt[N].toFixed(2)} m</text></g>
      <text x={x0} y={mT - 8} fill={CYAN} fontSize="8" fontWeight="800">CORTE A-A · {N} NIVEL{N > 1 ? "ES" : ""} · LOSA ALIGERADA 0.20 m · VIGAS h=0.40 m</text>
    </svg>
  );
}

const LAYER_DEFS = [
  { id: "EST", label: "EST · Estructura", color: CYAN_HOT },
  { id: "ARQ", label: "ARQ · Arquitectura", color: WALL },
  { id: "ACOT", label: "ACOT · Cotas", color: DIMC },
  { id: "MOB", label: "MOB · Mobiliario", color: "#a5f3fc" },
];

const STAGE_TOOLS = [
  { id: "puntero", Icon: MousePointer2, label: "Puntero" },
  { id: "mano", Icon: Hand, label: "Mano (Pan)" },
  { id: "rotar", Icon: RotateCw, label: "Rotar vista 90°" },
  { id: "cota", Icon: Ruler, label: "Capa de cotas" },
  { id: "zoomin", Icon: ZoomIn, label: "Acercar" },
  { id: "zoomout", Icon: ZoomOut, label: "Alejar" },
  { id: "fit", Icon: Crosshair, label: "Ajustar / Centrar" },
  { id: "pdf", Icon: FileDown, label: "Exportar PDF" },
];

// ── Lienzo Negro (Vacío) para slots purgados: solo A·01 está certificado (Comité 2026-06) ──
// Mantiene la rejilla técnica + infraestructura del lienzo (zoom/pan/capas/herramientas), sin geometría.
function PlanoEnPreparacion({ modeloId, distribId }) {
  const VW = 520, VH = 640;
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full block select-none" data-testid="vm-diseno-preparacion" role="img" aria-label="Diseño en preparación">
      <defs>
        <pattern id="prepFine" width="13" height="13" patternUnits="userSpaceOnUse"><path d="M13 0H0V13" fill="none" stroke="#ffffff" strokeOpacity="0.04" strokeWidth="0.5" /></pattern>
        <pattern id="prepCoarse" width="65" height="65" patternUnits="userSpaceOnUse"><path d="M65 0H0V65" fill="none" stroke="#38bdf8" strokeOpacity="0.09" strokeWidth="0.7" /></pattern>
      </defs>
      <rect x="0" y="0" width={VW} height={VH} fill={BG} />
      <rect x="0" y="0" width={VW} height={VH} fill="url(#prepFine)" />
      <rect x="0" y="0" width={VW} height={VH} fill="url(#prepCoarse)" />
      {/* Contorno de lote punteado (referencia, sin geometría interior) */}
      <rect x="52" y="48" width={VW - 104} height={VH - 150} fill="none" stroke={WALL} strokeOpacity="0.35" strokeWidth="1.2" strokeDasharray="8 6" />
      {/* Mensaje central */}
      <g textAnchor="middle">
        <circle cx={VW / 2} cy={VH / 2 - 26} r="26" fill="none" stroke={CYAN} strokeOpacity="0.5" strokeWidth="1.4" strokeDasharray="4 4" />
        <path d={`M ${VW / 2 - 9} ${VH / 2 - 26} h 18 M ${VW / 2} ${VH / 2 - 35} v 18`} stroke={CYAN} strokeWidth="1.6" strokeOpacity="0.7" />
        <text x={VW / 2} y={VH / 2 + 26} fill={INKL} fontSize="17" fontWeight="800" letterSpacing="1.2">DISEÑO EN PREPARACIÓN</text>
        <text x={VW / 2} y={VH / 2 + 48} fill="#67e8f9" fontSize="11" fontWeight="700" letterSpacing="0.5">MODELO {modeloId} · DISTRIBUCIÓN {distribId}</text>
        <text x={VW / 2} y={VH / 2 + 70} fill="#64748b" fontSize="9" fontWeight="600">Actualmente solo el Modelo A · Distribución 01 está certificado.</text>
      </g>
      <text x="20" y={VH - 22} fill="#64748b" fontSize="9" fontWeight="700">LIENZO TÉCNICO · REJILLA ACTIVA · SIN PLANO ASIGNADO</text>
    </svg>
  );
}


export default function VmDesignStage({ project, onSet, geo, ambientes = [], onAmbienteArea }) {
  const p = project;
  const uso = p.diseno?.usoPrimerPiso || "COCHERA";
  const modeloId = p.diseno?.modeloId || "A";
  const distribId = p.diseno?.distribucionId || "01";
  const A = Number(p?.terreno?.ancho) || 7.5, L = Number(p?.terreno?.largo) || 20;
  const pisos = Number(p?.niveles?.pisos) || 1;
  const alt1 = Number(p?.terreno?.altura1) || 3.0;
  const altT = Number(p?.terreno?.alturaTipica) || 2.7;
  const parapetoH = Number(p?.terreno?.alturaParapeto) || 0.9;
  const incP = p?.niveles?.incluirParapeto !== false;

  const [view, setView] = React.useState("PLANTA");
  const [tool, setTool] = React.useState("mano");
  const [zoom, setZoom] = React.useState(1);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const [rot, setRot] = React.useState(0);
  const [fs, setFs] = React.useState(false);
  const [layers, setLayers] = React.useState({ EST: true, ARQ: true, ACOT: true, MOB: true });
  const [sel, setSel] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  // Consola de Mando IA (Admin) + capacidades ampliables por la IA
  const { user } = useAuth();
  const isAdmin = (user?.role || "") === "admin";
  const [xray, setXray] = React.useState(false);
  const [accent, setAccent] = React.useState(null);
  const [wallScale, setWallScale] = React.useState(1);
  const [gridOn, setGridOn] = React.useState(true);
  const [stairsPlus, setStairsPlus] = React.useState(false);
  const [extraTools, setExtraTools] = React.useState([]);
  const [edits, setEdits] = React.useState({ offsets: {}, extraCols: [], extraWalls: [], customDims: [], proRooms: [] });
  const [svgBlocks, setSvgBlocks] = React.useState({});
  const [colLayout, setColLayout] = React.useState(null);
  const [undoStack, setUndoStack] = React.useState([]);
  const [aiOpen, setAiOpen] = React.useState(false);
  const [aiInput, setAiInput] = React.useState("");
  const [aiBusy, setAiBusy] = React.useState(false);
  const [aiLog, setAiLog] = React.useState([]);
  const aiInputRef = React.useRef(null);
  const vpRef = React.useRef(null);
  const drag = React.useRef(null);
  const isCuratedA01 = modeloId === "A" && distribId === "01"; // ÚNICO diseño certificado (Comité 2026-06)
  const isPurged = !isCuratedA01;                               // resto de slots → Lienzo Negro "Diseño en preparación"
  const editable = tool === "puntero" && view === "PLANTA" && !isCuratedA01 && false;
  // Biblioteca de Élite: carga los bloques SVG maestros redibujados por la IA (heredados por todos).
  React.useEffect(() => {
    let alive = true;
    api.get("/vm/estructural/ai-console/blocks").then(({ data }) => { if (alive) setSvgBlocks(data?.blocks || {}); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Ley de Ejes: lee la grilla estructural (C-01/C-02/C-03) del backend, reactiva a las medidas del terreno.
  React.useEffect(() => {
    let alive = true;
    api.get(`/vm/estructural/column-layout?ancho=${A}&largo=${L}`).then(({ data }) => { if (alive && data?.ok) setColLayout(data); }).catch(() => {});
    return () => { alive = false; };
  }, [A, L]);
  const ambEff = preview && ambientes[preview.i] ? ambientes.map((a, i) => (i === preview.i ? { ...a, m2: preview.m2 } : a)) : ambientes;

  // Editor bidireccional: arrastrar manejador de muro → nueva área → tabla Bloque 4.
  const handleDown = (i, axis, edge, room, e) => {
    e.stopPropagation();
    const sign = edge === "r" || edge === "b" ? 1 : -1;
    const base = axis === "x" ? room.w : room.h;
    const m2 = (preview && preview.i === i ? preview.m2 : ambientes[i]?.m2) || 1;
    const start = axis === "x" ? e.clientX : e.clientY;
    const z = Math.max(0.4, zoom);
    const move = (ev) => { const cur = axis === "x" ? ev.clientX : ev.clientY; const d = (sign * (cur - start)) / z; const factor = Math.max(0.2, (base + d) / base); setPreview({ i, m2: Math.round(m2 * factor * 100) / 100 }); };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); setPreview((pv) => { if (pv && ambientes[pv.i] && onAmbienteArea) onAmbienteArea(ambientes[pv.i].id, pv.m2); return null; }); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const fit = () => { setZoom(1); setPan({ x: 0, y: 0 }); setRot(0); };
  // Ajustar y Centrar: recalcula zoom para mostrar TODO el pliego y lo re-centra con pan (robusto ante aspect alto).
  const fitAll = () => {
    setRot(0);
    const vp = vpRef.current;
    if (!vp) { setZoom(1); setPan({ x: 0, y: 0 }); return; }
    const svg = vp.querySelector('svg[data-testid="vm-planta-modelo-a"]') || vp.querySelector("svg");
    if (!svg) { setZoom(1); setPan({ x: 0, y: 0 }); return; }
    const vb = vp.getBoundingClientRect();
    const sb = svg.getBoundingClientRect();
    const z0 = zoom || 1;
    const natW = sb.width / z0, natH = sb.height / z0;
    if (!natW || !natH) { setZoom(1); setPan({ x: 0, y: 0 }); return; }
    const z = clamp(+Math.min((vb.width * 0.92) / natW, (vb.height * 0.92) / natH).toFixed(3), 0.25, 3);
    setZoom(z);
    // Tras aplicar el zoom, re-centrar el svg dentro del viewport mediante pan (tras la transición).
    setTimeout(() => {
      const el = vpRef.current; if (!el) return;
      const s2 = (el.querySelector('svg[data-testid="vm-planta-modelo-a"]') || el.querySelector("svg"));
      if (!s2) return;
      const v2 = el.getBoundingClientRect(), b2 = s2.getBoundingClientRect();
      const dx = (v2.x + v2.width / 2) - (b2.x + b2.width / 2);
      const dy = (v2.y + v2.height / 2) - (b2.y + b2.height / 2);
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    }, 300);
  };
  // Auto-encuadre al cambiar las medidas del terreno (o al entrar a A-01 en pantalla completa).
  React.useEffect(() => {
    if (!isCuratedA01 || view !== "PLANTA") return;
    const t = setTimeout(fitAll, 220);
    return () => clearTimeout(t);
  }, [A, L, fs, isCuratedA01, colLayout]);
  // Zoom ANCLADO AL CENTRO del plano (Mandato UX): sólo cambia la escala; el pan se mantiene y, como
  // transformOrigin = center center, el centro del dibujo permanece fijo en el visor (sin derivar a esquinas).
  const zoomCentered = (factor) => setZoom((z) => clamp(+(z * factor).toFixed(3), 0.4, 4));
  // Zoom con rueda (listener no-pasivo) · anclado al centro con INVERSIÓN de sentido:
  // giro hacia adelante (deltaY<0) = Alejar (Zoom Out); giro hacia atrás (deltaY>0) = Acercar (Zoom In).
  React.useEffect(() => {
    const el = vpRef.current; if (!el) return;
    const onWheel = (e) => { e.preventDefault(); zoomCentered(e.deltaY < 0 ? 1 / 1.1 : 1.1); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [fs]);
  // Pan con arrastre
  const onDown = (e) => { if (e.target.closest("button")) return; drag.current = { sx: e.clientX, sy: e.clientY, px: pan.x, py: pan.y }; try { e.currentTarget.setPointerCapture(e.pointerId); } catch (x) {} };
  const onMove = (e) => { if (!drag.current) return; setPan({ x: drag.current.px + (e.clientX - drag.current.sx), y: drag.current.py + (e.clientY - drag.current.sy) }); };
  const onUp = () => { drag.current = null; };

  const onTool = (id) => {
    if (id === "zoomin") return zoomCentered(1.2);
    if (id === "zoomout") return zoomCentered(1 / 1.2);
    if (id === "fit") return fitAll();
    if (id === "rotar") return setRot((x) => (x + 90) % 360);
    if (id === "cota") return setLayers((L2) => ({ ...L2, ACOT: !L2.ACOT }));
    if (id === "pdf") return exportExpedientePDF(p, { modeloId, uso });
    setTool(id);
  };

  const tabBtn = (on, accent) => ({ className: `px-2.5 py-1 text-[10px] font-extrabold tracking-wide transition-colors ${on ? "text-white" : "text-slate-200 hover:bg-white/10"}`, style: { background: on ? accent : "transparent" } });

  // ── Ejecutor de acciones de la IA (catálogo curado; integridad garantizada) ──
  const applyAction = (a) => {
    switch (a.action) {
      case "set_view": if (["PLANTA", "ELEVACION", "CORTE", "3D"].includes(a.value)) setView(a.value); return `Vista → ${a.value}`;
      case "set_model": if (["A", "B", "C"].includes(a.value)) onSet("diseno.modeloId", a.value); return `Modelo → ${a.value}`;
      case "set_distrib": if (["01", "02", "03"].includes(a.value)) onSet("diseno.distribucionId", a.value); return `Distribución → ${a.value}`;
      case "set_uso": if (["COCHERA", "TIENDA"].includes(a.value)) onSet("diseno.usoPrimerPiso", a.value); return `Uso frontal → ${a.value}`;
      case "set_layer": if (["EST", "ARQ", "MOB", "ACOT"].includes(a.id)) setLayers((L) => ({ ...L, [a.id]: a.on !== false })); return `Capa ${a.id} → ${a.on !== false ? "ON" : "OFF"}`;
      case "zoom": zoomCentered(a.value === "out" ? 1 / 1.25 : 1.25); return `Zoom ${a.value === "out" ? "−" : "+"}`;
      case "fit": fit(); return "Vista ajustada";
      case "fullscreen": setFs(!!a.on); return `Pantalla completa ${a.on ? "ON" : "OFF"}`;
      case "toggle_xray": { const on = a.on !== false; setXray(on); if (on) setLayers((L) => ({ ...L, EST: true })); return `Rayos X ${on ? "ACTIVADOS" : "desactivados"}`; }
      case "set_accent": if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(a.value || "")) { setAccent(a.value); } return `Acento → ${a.value}`;
      case "set_wall_thickness": { const v = Math.max(0.6, Math.min(1.6, Number(a.value) || 1)); setWallScale(v); return `Grosor de muro ×${v}`; }
      case "toggle_grid": setGridOn(a.on !== false); return `Rejilla ${a.on !== false ? "ON" : "OFF"}`;
      case "improve_stairs": setStairsPlus(true); return "Escalera técnica mejorada";
      case "activate_tool": { const id = String(a.id || "tool"); setExtraTools((t) => (t.find((x) => x.id === id) ? t : [...t, { id, label: a.label || id }])); return `Herramienta activada: ${a.label || id}`; }
      case "move_ambiente": { const key = String(a.target || "").trim(); const dx = Number(a.dx) || 0, dy = Number(a.dy) || 0; if (!key) return null; setEdits((E) => { const prev = E.offsets[key] || { dx: 0, dy: 0 }; return { ...E, offsets: { ...E.offsets, [key]: { dx: prev.dx + dx, dy: prev.dy + dy } } }; }); return `Ambiente '${key}' movido (${dx >= 0 ? "+" : ""}${dx.toFixed(2)}, ${dy >= 0 ? "+" : ""}${dy.toFixed(2)}) m`; }
      case "duplicate_element": { const el = String(a.element || "columna").toLowerCase(); if (/muro|wall/.test(el)) { setEdits((E) => ({ ...E, extraWalls: [...E.extraWalls, { x1: Number(a.x) || 0.25, y1: Number(a.y) || 0.25, x2: Number(a.x2) || 6.75, y2: Number(a.y2) || 0.25 }] })); return "Muro duplicado"; } setEdits((E) => ({ ...E, extraCols: [...E.extraCols, { x: Number(a.x) || 3.5, y: Number(a.y) || 10 }] })); return "Columna duplicada"; }
      case "add_dimension": { const dim = a.side ? { side: String(a.side).toLowerCase(), label: a.label } : { x1: Number(a.x1) || 0, y1: Number(a.y1) || 0, x2: Number(a.x2) || 0, y2: Number(a.y2) || 0, label: a.label }; setEdits((E) => ({ ...E, customDims: [...E.customDims, dim] })); return `Cota añadida${a.side ? " (" + a.side + ")" : ""}`; }
      case "upgrade_furniture": { const key = String(a.target || "").trim(); if (!key) return null; setEdits((E) => (E.proRooms.includes(key) ? E : { ...E, proRooms: [...E.proRooms, key] })); return `Mobiliario Pro en '${key}'`; }
      case "set_block_svg": { const blk = String(a.block || "").toLowerCase().trim(); if (!blk || !a.svg) return null; setSvgBlocks((m) => ({ ...m, [blk]: a.svg })); setLayers((L) => ({ ...L, MOB: true })); return `Bloque '${blk}' rediseñado por IA (biblioteca de élite)`; }
      case "note": return a.text || "Sin acción.";
      default: return null;
    }
  };

  const snapshot = () => ({
    view, layers: { ...layers }, uso, modeloId, distribId, xray, accent, wallScale, gridOn, stairsPlus,
    extraTools: [...extraTools], svgBlocks: { ...svgBlocks },
    edits: { offsets: { ...edits.offsets }, extraCols: [...edits.extraCols], extraWalls: [...edits.extraWalls], customDims: [...edits.customDims], proRooms: [...edits.proRooms] },
  });
  const restore = (s) => {
    if (!s) return;
    setView(s.view); setLayers(s.layers); setXray(s.xray); setAccent(s.accent);
    setWallScale(s.wallScale); setGridOn(s.gridOn); setStairsPlus(s.stairsPlus);
    setExtraTools(s.extraTools); setEdits(s.edits);
    if (s.svgBlocks) setSvgBlocks(s.svgBlocks);
    if (s.uso !== uso) onSet("diseno.usoPrimerPiso", s.uso);
    if (s.modeloId !== modeloId) onSet("diseno.modeloId", s.modeloId);
    if (s.distribId !== distribId) onSet("diseno.distribucionId", s.distribId);
  };
  const undoLast = () => {
    setUndoStack((st) => {
      if (!st.length) return st;
      const [last, ...rest] = st;
      restore(last);
      setAiLog((L) => [{ t: Date.now(), q: "Deshacer último comando", note: "Reversión aplicada.", undo: true }, ...L].slice(0, 8));
      return rest;
    });
  };

  const runAiCommand = async () => {
    const cmd = aiInput.trim();
    if (!cmd || aiBusy) return;
    const snap = snapshot();
    setAiBusy(true);
    try {
      const ctx = { view, modelo: modeloId, distribucion: distribId, uso, layers, xray, zoom: Math.round(zoom * 100), isCuratedA01, ambientes: (ambientes || []).map((a) => a.nombre) };
      const { data } = await Promise.resolve({ data: { actions: [], note: "Consola IA retirada (P4 · V1.1.1)." } });
      const results = (data.actions || []).map((a) => { const r = applyAction(a); return r ? (a.reason ? `${r} — ${a.reason}` : r) : null; }).filter(Boolean);
      const note = data.note || (results.length ? "Comando ejecutado." : "Sin acciones aplicables.");
      if (results.length) setUndoStack((st) => [snap, ...st].slice(0, 12));
      setAiLog((L) => [{ t: Date.now(), q: cmd, note, done: results }, ...L].slice(0, 8));
      setAiInput("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al procesar el comando.";
      setAiLog((L) => [{ t: Date.now(), q: cmd, note: msg, err: true }, ...L].slice(0, 8));
    } finally {
      setAiBusy(false);
      setTimeout(() => aiInputRef.current?.focus(), 30);
    }
  };

  // Atajo Ctrl+K (solo Admin) para abrir la consola.
  React.useEffect(() => {
    if (!isAdmin) return;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault(); setAiOpen(true); setTimeout(() => aiInputRef.current?.focus(), 40);
      } else if (e.key === "Escape" && aiOpen) { setAiOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAdmin, aiOpen]);

  const EXTRA_ICON = { offset: CornerUpRight, medir: Ruler, texto: TypeIcon };

  const Stage = (
    <div className={`relative w-full ${fs ? "h-full" : ""}`} data-testid="vm-lienzo-maestro">
      {/* Viewport (zoom/pan) */}
      <div ref={vpRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        className="relative overflow-hidden rounded-xl border-2 border-[#0b3a4a]"
        style={{ background: BG, cursor: drag.current ? "grabbing" : "grab", minHeight: fs ? "calc(100vh - 90px)" : 500, height: fs ? "calc(100vh - 90px)" : 500, borderColor: xray ? "#f59e0b" : (accent || undefined), boxShadow: xray ? "inset 0 0 60px rgba(0,255,255,0.12)" : undefined }}
        data-testid="vm-canvas-viewport">
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${view === "PLANTA" ? rot : 0}deg)`, transformOrigin: "center center", width: "88%", transition: drag.current ? "none" : "transform .22s cubic-bezier(0.22,1,0.36,1)" }}>
            {isPurged ? (
              <PlanoEnPreparacion modeloId={modeloId} distribId={distribId} />
            ) : (
              <>
                {view === "PLANTA" && <PlantaModeloAFuncional layers={layers} ambientes={ambEff} uso={uso} anchoReal={A} largoReal={L} xray={xray} wallScale={wallScale} gridOn={gridOn} stairsPlus={stairsPlus} accent={accent} edits={edits} svgBlocks={svgBlocks} columns={colLayout?.columns} colCounts={colLayout?.counts} variant="01" />}
                {view === "ELEVACION" && <div className="bg-[#0e1621] rounded-lg p-2"><ElevacionTecnica ancho={A} pisos={pisos} altura1={alt1} alturaTipica={altT} parapeto={parapetoH} incluirParapeto={incP} modeloId={modeloId} uso={uso} /></div>}
                {view === "CORTE" && <CorteAA largo={L} pisos={pisos} altura1={alt1} alturaTipica={altT} incluirParapeto={incP} parapetoH={parapetoH} />}
                {view === "3D" && <img src={(VM_FACADES[modeloId] || VM_FACADES.A).img} alt="Render 3D" className="rounded-lg bg-white w-full" style={{ maxHeight: 460, objectFit: "contain" }} />}
              </>
            )}
          </div>
        </div>

        {/* Brújula NORTE (inf-izq del lienzo) — oculta en A-01 (el pliego trae su propia rosa) */}
        {!isCuratedA01 && (
        <div className="absolute bottom-14 left-14 z-20 text-cyan-200/80 pointer-events-none" aria-hidden="true">
          <svg width="34" height="34" viewBox="0 0 34 34"><circle cx="17" cy="17" r="15" fill="none" stroke="#22d3ee" strokeOpacity="0.4" /><path d="M17 3 L21 17 L17 15 L13 17 Z" fill="#22d3ee" /><text x="17" y="30" textAnchor="middle" fontSize="7" fill="#67e8f9" fontWeight="800">N</text></svg>
        </div>
        )}

        {/* Barra de estado inferior (industrial) */}
        <div className="absolute left-11 right-0 bottom-0 z-20 flex items-center gap-4 px-3 py-1 bg-[#061a22]/95 border-t border-cyan-400/20 text-[9px] font-bold text-cyan-100/70 pointer-events-none" data-testid="vm-status-bar">
          <span>ESCALA 1:50</span><span>REJILLA: ON</span><span>UNIDAD: m</span><span>TERRENO: {A.toFixed(2)} × {L.toFixed(2)} m</span><span className="text-cyan-300">ZOOM: {Math.round(zoom * 100)}%</span>
        </div>

        {/* Cinta de herramientas (izquierda) */}
        <div className="absolute left-0 top-0 bottom-0 w-11 bg-[#08222c]/95 border-r border-cyan-400/20 flex flex-col items-center gap-1 py-2 z-30" data-testid="vm-cad-toolbar">
          {STAGE_TOOLS.map(({ id, Icon, label }) => (
            <button key={id} title={label} data-testid={`vm-cad-${id}`} onClick={() => onTool(id)}
              className={`grid place-items-center h-8 w-8 rounded-md transition-colors ${tool === id ? "bg-cyan-400 text-slate-900" : "text-cyan-100/80 hover:bg-cyan-400/15"}`}><Icon size={15} /></button>
          ))}
          {extraTools.length > 0 && <div className="my-1 h-px w-6 bg-cyan-400/25" />}
          {extraTools.map((t) => {
            const Icon = EXTRA_ICON[t.id] || Sparkles;
            return (
              <button key={t.id} title={`${t.label} (IA)`} data-testid={`vm-cad-extra-${t.id}`} onClick={() => setTool(t.id)}
                className={`relative grid place-items-center h-8 w-8 rounded-md transition-colors ${tool === t.id ? "bg-amber-400 text-slate-900" : "text-amber-200/90 hover:bg-amber-400/15"}`}>
                <Icon size={15} /><span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-400" />
              </button>
            );
          })}
        </div>

        {/* Insignia RAYOS X (sup-centro) */}
        {xray && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 rounded-full bg-amber-400/95 text-slate-900 px-3 py-1 text-[10px] font-extrabold shadow-lg" data-testid="vm-xray-badge">
            <Sparkles size={12} /> RAYOS X
          </div>
        )}

        {/* Selectores modelo + distribución (sup-izq) */}
        <div className="absolute top-2 left-14 z-30 flex flex-col gap-1.5">
          <div className="inline-flex rounded-lg overflow-hidden border border-cyan-400/25 bg-[#08222c]/90 shadow-lg" data-testid="vm-modelos">
            {["A", "B", "C"].map((id) => <button key={id} data-testid={`vm-modelo-${id}`} onClick={() => onSet("diseno.modeloId", id)} {...tabBtn(modeloId === id, MODEL_ACCENT[id])}>MODELO {id}</button>)}
          </div>
          <div className="inline-flex rounded-lg overflow-hidden border border-cyan-400/25 bg-[#08222c]/90 shadow-lg" data-testid="vm-distribuciones">
            {["01", "02", "03"].map((id) => <button key={id} data-testid={`vm-distrib-${id}`} onClick={() => onSet("diseno.distribucionId", id)} {...tabBtn(distribId === id, "#0ea5b7")}>DISTRIB. {id}</button>)}
          </div>
        </div>

        {/* Uso frontal (sup-der, junto a capas) */}
        <div className="absolute top-2 right-3 z-30 inline-flex rounded-lg overflow-hidden border border-cyan-400/25 bg-[#08222c]/90 shadow-lg" data-testid="vm-uso-toggle">
          {["COCHERA", "TIENDA"].map((u) => <button key={u} data-testid={`vm-uso-${u.toLowerCase()}`} onClick={() => onSet("diseno.usoPrimerPiso", u)} {...tabBtn(uso === u, "#0ea5b7")}>{u}</button>)}
        </div>

        {/* Panel de CAPAS (derecha) */}
        <div className="absolute top-12 right-3 z-30 w-40 rounded-lg border border-cyan-400/25 bg-[#08222c]/92 shadow-lg p-2" data-testid="vm-layers-panel">
          <div className="flex items-center gap-1.5 text-[10px] font-extrabold text-cyan-200 mb-1.5"><LayersIcon size={12} /> CAPAS</div>
          {LAYER_DEFS.map((ly) => {
            const on = layers[ly.id];
            return (
              <button key={ly.id} data-testid={`vm-layer-${ly.id}`} onClick={() => setLayers((L2) => ({ ...L2, [ly.id]: !L2[ly.id] }))}
                className="w-full flex items-center justify-between gap-2 px-1.5 py-1 rounded-md hover:bg-white/5 transition-colors">
                <span className="flex items-center gap-1.5 text-[9.5px] font-bold" style={{ color: on ? ly.color : "#64748b" }}><span className="h-2 w-2 rounded-sm" style={{ background: on ? ly.color : "#334155", boxShadow: on && ly.id === "EST" ? `0 0 6px ${ly.color}` : "none" }} />{ly.label}</span>
                {on ? <Eye size={12} className="text-cyan-200" /> : <EyeOff size={12} className="text-slate-500" />}
              </button>
            );
          })}
        </div>

        {/* Leyenda de símbolos técnicos (oficializa el plano) */}
        <div className="absolute right-3 z-30 w-40 rounded-lg border border-cyan-400/25 bg-[#08222c]/92 shadow-lg p-2 text-[8.5px] text-cyan-100/85" style={{ top: 210 }} data-testid="vm-legend">
          <div className="font-extrabold text-cyan-200 mb-1 tracking-wide">LEYENDA</div>
          {[["Muro 0.15/0.25 m", WALL, "wall"], ["Estructura (viga/col.)", CYAN_HOT, "est"], ["Ventana", MODEL_ACCENT[modeloId], "win"], ["Cota / N.P.T.", DIMC, "dim"], ["Mobiliario", "#a5f3fc", "mob"]].map(([txt, col, k]) => (
            <div key={k} className="flex items-center gap-1.5 py-[1px]"><span className="inline-block h-2 w-3 rounded-[1px]" style={{ background: col }} />{txt}</div>
          ))}
          {editable && <div className="mt-1 pt-1 border-t border-cyan-400/20 text-[8px] text-cyan-300">✎ Arrastra los puntos del ambiente para redimensionar</div>}
        </div>

        {/* Conmutador de vista (inf-izq) */}
        <div className="absolute bottom-2 left-14 z-30 inline-flex rounded-lg overflow-hidden border border-cyan-400/25 bg-[#08222c]/90 shadow-lg" data-testid="vm-vista-toggle">
          {[["PLANTA", "Planta"], ["ELEVACION", "Elevación"], ["CORTE", "Corte A-A"], ["3D", "3D"]].map(([k, lbl]) => <button key={k} data-testid={`vm-vista-${k.toLowerCase()}`} onClick={() => setView(k)} {...tabBtn(view === k, "#0284c7")}>{lbl}</button>)}
        </div>

        {/* Zoom % + Ajustar y Centrar + Maximizar (inf-der) */}
        <div className="absolute bottom-2 right-3 z-30 flex items-center gap-2">
          <button data-testid="vm-fit-center" title="Ajustar y Centrar toda la estructura" onClick={fitAll}
            className="rounded-md bg-[#08222c]/90 border border-cyan-400/25 px-2 py-1 text-[9px] font-bold text-cyan-200 hover:bg-cyan-400/15 transition-colors">Ajustar y Centrar</button>
          <span className="rounded-md bg-[#08222c]/90 border border-cyan-400/25 px-2 py-1 text-[9px] font-bold text-cyan-200" data-testid="vm-zoom-indicator">{Math.round(zoom * 100)}%</span>
          <button data-testid="vm-fullscreen-toggle" title="Maximizar" onClick={() => setFs((v) => !v)} className="grid place-items-center h-7 w-7 rounded-md bg-cyan-400 text-slate-900 hover:brightness-110">{fs ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</button>
        </div>

        {/* ── CONSOLA DE MANDO IA · exclusiva del Administrador (botón + Ctrl+K) ── */}
        {false /* Consola IA retirada · P4 V1.1.1 */ && (
          <button data-testid="vm-ai-console-open" onClick={() => { setAiOpen(true); setTimeout(() => aiInputRef.current?.focus(), 40); }}
            className="absolute bottom-11 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-cyan-400 text-slate-900 px-4 py-1.5 text-[11px] font-extrabold shadow-xl hover:brightness-110 transition">
            <Sparkles size={13} /> Consola IA <span className="rounded bg-slate-900/25 px-1.5 py-0.5 text-[9px] font-bold">Ctrl+K</span>
          </button>
        )}
        {false /* Consola IA retirada · P4 V1.1.1 */ && (
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-40 w-[min(560px,92%)] rounded-xl border border-amber-400/40 bg-[#061018]/97 backdrop-blur shadow-2xl overflow-hidden" data-testid="vm-ai-console">
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-amber-400/20 bg-[#08222c]/80">
              <Terminal size={13} className="text-amber-300" />
              <span className="text-[11px] font-extrabold text-amber-200 tracking-wide">CEREBRO DEL ADMINISTRADOR · Operador Inteligente (Claude Sonnet 4.6)</span>
              <button data-testid="vm-ai-undo" onClick={undoLast} disabled={!undoStack.length} title={undoStack.length ? "Deshacer último comando IA" : "No hay comandos para deshacer"}
                className="ml-auto flex items-center gap-1 rounded-md bg-slate-700/70 text-cyan-100 px-2 py-0.5 text-[10px] font-bold disabled:opacity-35 hover:bg-slate-600 transition">
                <RotateCw size={11} style={{ transform: "scaleX(-1)" }} /> Deshacer{undoStack.length ? ` (${undoStack.length})` : ""}
              </button>
              <button data-testid="vm-ai-console-close" onClick={() => setAiOpen(false)} className="text-slate-400 hover:text-white"><X size={14} /></button>
            </div>
            {/* Log minimalista de ejecución */}
            {aiLog.length > 0 && (
              <div className="max-h-36 overflow-auto px-3 py-2 space-y-1.5 text-[10.5px]" data-testid="vm-ai-log">
                {aiLog.map((l) => (
                  <div key={l.t} className="leading-snug border-b border-white/5 pb-1.5 last:border-0">
                    <div className="text-slate-400"><span className="text-cyan-300/80">›</span> {l.q}</div>
                    <div className={`font-bold ${l.err ? "text-rose-300" : l.undo ? "text-amber-300" : "text-emerald-300"}`}>{l.err ? "⚠" : l.undo ? "↺" : "✓"} {l.undo ? l.note : `Comando ejecutado: ${l.note}`}</div>
                    {l.done && l.done.length > 0 && <div className="text-slate-500 text-[9px]">{l.done.join(" · ")}</div>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 px-2.5 py-2 border-t border-amber-400/15">
              <Sparkles size={14} className="text-amber-300 shrink-0" />
              <input ref={aiInputRef} data-testid="vm-ai-input" value={aiInput} onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runAiCommand(); }}
                placeholder="Ej.: Activa Rayos X y muestra la elevación…"
                className="flex-1 bg-transparent outline-none text-[12px] text-cyan-50 placeholder:text-slate-500" />
              <button data-testid="vm-ai-run" onClick={runAiCommand} disabled={aiBusy || !aiInput.trim()}
                className="rounded-lg bg-amber-400 text-slate-900 px-3 py-1 text-[11px] font-extrabold disabled:opacity-40 hover:brightness-110 transition">
                {aiBusy ? "Procesando…" : "Ejecutar"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* SVGs ocultos para el PDF */}
      <div style={{ position: "absolute", left: -9999, top: -9999, width: 460 }} aria-hidden="true">
        {view !== "PLANTA" && <PlantaBlueprint ancho={A} largo={L} uso={uso} modeloId={modeloId} layers={{ EST: true, ARQ: true, ACOT: true, MOB: true }} ambientes={ambientes} />}
        {view !== "ELEVACION" && <ElevacionTecnica ancho={A} pisos={pisos} altura1={alt1} alturaTipica={altT} parapeto={parapetoH} incluirParapeto={incP} modeloId={modeloId} uso={uso} />}
      </div>
    </div>
  );

  if (fs) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/92 backdrop-blur-sm p-3 flex flex-col" data-testid="vm-fullscreen-stage">
        <div className="flex items-center justify-between px-1 pb-2">
          <div className="text-cyan-200 text-xs font-extrabold tracking-wide">COREX · LIENZO MAESTRO · {p?.nombre || "VIVIENDA MULTIFAMILIAR"}</div>
          <button onClick={() => setFs(false)} data-testid="vm-fullscreen-close" className="flex items-center gap-1.5 rounded-lg bg-cyan-400 text-slate-900 px-3 py-1.5 text-xs font-bold hover:brightness-110"><Minimize2 size={14} /> Salir de pantalla completa</button>
        </div>
        <div className="flex-1 min-h-0">{Stage}</div>
      </div>
    );
  }
  return Stage;
}
